import React, { useState, useMemo, useEffect, useCallback } from "react";
import useMetadata from "app/hooks/useMetadata";
import CodeExecutionPanel from "components/CodeEditorAndOutput/CodeExecutionPanel";

const ProblemSubmissionTemplates = ({
  problemData,
  selectedLanguage: externalSelectedLanguage,
  setSelectedLanguage: setExternalSelectedLanguage,
  editorLanguage,
  setProblemData,
}) => {
  const { metadata, loading: metadataLoading } = useMetadata();
  const [codeByLanguage, setCodeByLanguage] = useState({});

  const activeTemplates = useMemo(
    () =>
      problemData?.problemSubmissionTemplates?.filter((t) => t.active) || [],
    [problemData]
  );

  const availableLanguages = useMemo(
    () =>
      metadata && metadata.programmingLanguages
        ? metadata.programmingLanguages
        : [],
    [metadata]
  );

  useEffect(() => {
    if (metadataLoading || !metadata?.programmingLanguages) {
      return;
    }

    if (externalSelectedLanguage) {
      return;
    }

    let langToSet = null;

    if (activeTemplates.length > 0) {
      const firstActiveTemplate = activeTemplates[0];
      langToSet = availableLanguages.find(
        (lang) => lang.key === firstActiveTemplate.programmingLanguage
      );
      if (langToSet) {
        setExternalSelectedLanguage(langToSet);
        setCodeByLanguage((prev) => ({
          ...prev,
          [langToSet.key]:
            prev[langToSet.key] || firstActiveTemplate.template || "",
        }));
      }
    }

    if (!langToSet && availableLanguages.length > 0) {
      langToSet = availableLanguages[0];
      setExternalSelectedLanguage(langToSet);
      setCodeByLanguage((prev) => ({
        ...prev,
        [langToSet.key]: prev[langToSet.key] || "",
      }));
    }
  }, [
    metadataLoading,
    metadata,
    activeTemplates,
    availableLanguages,
    externalSelectedLanguage,
    setExternalSelectedLanguage,
    codeByLanguage,
  ]);

  const handleReset = useCallback(() => {
    if (externalSelectedLanguage) {
      const template =
        activeTemplates.find(
          (t) => t.programmingLanguage === externalSelectedLanguage.key
        )?.template || "";
      setCodeByLanguage((prev) => ({
        ...prev,
        [externalSelectedLanguage.key]: template,
      }));
      setProblemData((prev) => {
        const templates = [...prev.problemSubmissionTemplates];
        const index = templates.findIndex(
          (t) => t.programmingLanguage === externalSelectedLanguage.key
        );
        if (index !== -1) {
          templates[index] = {
            ...templates[index],
            template: template,
          };
          return { ...prev, problemSubmissionTemplates: templates };
        }
        return prev;
      });
    } else {
      setCodeByLanguage({});
    }
  }, [externalSelectedLanguage, activeTemplates, setProblemData]);

  const handleEditorChange = useCallback(
    (newValue) => {
      if (externalSelectedLanguage) {
        setCodeByLanguage((prev) => ({
          ...prev,
          [externalSelectedLanguage.key]: newValue,
        }));
        setProblemData((prev) => {
          const templates = [...prev.problemSubmissionTemplates];
          const index = templates.findIndex(
            (t) => t.programmingLanguage === externalSelectedLanguage.key
          );
          if (index !== -1) {
            templates[index] = {
              ...templates[index],
              template: newValue,
            };
          } else {
            templates.push({
              template: newValue,
              programmingLanguage: externalSelectedLanguage.key,
              description: "",
              active: true,
            });
          }
          return { ...prev, problemSubmissionTemplates: templates };
        });
      }
    },
    [externalSelectedLanguage, setProblemData]
  );

  const handleLanguageChange = useCallback(
    (e) => {
      const lang = e.target.value;
      if (lang) {
        setExternalSelectedLanguage(lang);
        const template =
          activeTemplates.find((t) => t.programmingLanguage === lang.key)
            ?.template || "";
        const codeToSet = codeByLanguage[lang.key] || template;
        setCodeByLanguage((prev) => ({
          ...prev,
          [lang.key]: codeToSet,
        }));
        setProblemData((prev) => {
          const templates = [...prev.problemSubmissionTemplates];
          const index = templates.findIndex(
            (t) => t.programmingLanguage === lang.key
          );
          if (index === -1) {
            templates.push({
              template: codeToSet,
              programmingLanguage: lang.key,
              description: "",
              active: true,
            });
          } else {
            templates[index] = {
              ...templates[index],
              template: codeToSet,
            };
          }
          return { ...prev, problemSubmissionTemplates: templates };
        });
      }
    },
    [
      activeTemplates,
      codeByLanguage,
      setExternalSelectedLanguage,
      setProblemData,
    ]
  );

  const handleActiveChange = useCallback(
    (event) => {
      if (externalSelectedLanguage) {
        setProblemData((prev) => {
          const templates = [...prev.problemSubmissionTemplates];
          const index = templates.findIndex(
            (t) => t.programmingLanguage === externalSelectedLanguage.key
          );
          const currentCode =
            codeByLanguage[externalSelectedLanguage.key] || "";
          if (index !== -1) {
            templates[index] = {
              ...templates[index],
              active: event.target.checked,
              template: currentCode,
            };
          } else {
            templates.push({
              template: currentCode,
              programmingLanguage: externalSelectedLanguage.key,
              description: "",
              active: event.target.checked,
            });
          }
          return { ...prev, problemSubmissionTemplates: templates };
        });
      }
    },
    [externalSelectedLanguage, codeByLanguage, setProblemData]
  );

  const isActive = externalSelectedLanguage
    ? activeTemplates.find(
        (t) => t.programmingLanguage === externalSelectedLanguage.key
      )?.active ?? false
    : false;

  const currentCode = externalSelectedLanguage
    ? codeByLanguage[externalSelectedLanguage.key] || ""
    : "";

  return (
    <CodeExecutionPanel
      problemData={problemData}
      code={currentCode}
      setCode={handleEditorChange}
      selectedLanguage={externalSelectedLanguage}
      setSelectedLanguage={setExternalSelectedLanguage}
      editorLanguage={editorLanguage}
      activeTemplates={activeTemplates}
      availableLanguages={availableLanguages}
      showSubmissionTab={false}
      onReset={handleReset}
      onLanguageChange={handleLanguageChange}
      onEditorChange={handleEditorChange}
      onActiveChange={handleActiveChange}
      isActive={isActive}
      setProblemData={setProblemData}
    />
  );
};

export default ProblemSubmissionTemplates;
